import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { 
  Archive, 
  Clock, 
  Settings, 
  Play, 
  Pause,
  AlertTriangle,
  CheckCircle,
  Calendar,
  FileText
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ArchivalRule {
  id: string;
  name: string;
  description: string;
  trigger_type: 'age' | 'compliance_score' | 'legal_flag' | 'manual';
  trigger_value: string;
  action: 'archive' | 'lock' | 'flag_review';
  is_active: boolean;
  last_run: string;
  items_processed: number;
}

interface ArchivalCandidate {
  content_id: string;
  title: string;
  created_date: string;
  last_accessed: string;
  compliance_score: number;
  legal_flags: string[];
  recommended_action: string;
  reason: string;
}

const AutomatedArchivalSystem: React.FC = () => {
  const [rules, setRules] = useState<ArchivalRule[]>([]);
  const [candidates, setCandidates] = useState<ArchivalCandidate[]>([]);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_type: 'age' as const,
    trigger_value: '',
    action: 'archive' as const
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchArchivalRules();
    fetchArchivalCandidates();
  }, []);

  // Pull "rules" from nodes/violations heuristically (since no archival_rules table exists)
  const fetchArchivalRules = async () => {
    // Simulate live rule mapping (just for UI, since you lack rules table)
    // Could expand if you want to persist rules!
    const ruleTemplates: ArchivalRule[] = [
      {
        id: 'age-1y',
        name: 'Archive Old Content',
        description: 'Archive content older than 1 year',
        trigger_type: 'age',
        trigger_value: '365',
        action: 'archive',
        is_active: true,
        last_run: new Date().toISOString(),
        items_processed: 0,
      },
      {
        id: 'compliance-60',
        name: 'Lock Low Compliance',
        description: 'Lock content with compliance score below 60',
        trigger_type: 'compliance_score',
        trigger_value: '60',
        action: 'lock',
        is_active: true,
        last_run: new Date().toISOString(),
        items_processed: 0,
      },
      {
        id: 'critical-flags',
        name: 'Archive Legal Flags',
        description: 'Immediately archive content with critical legal flags',
        trigger_type: 'legal_flag',
        trigger_value: 'critical',
        action: 'archive',
        is_active: true,
        last_run: new Date().toISOString(),
        items_processed: 0,
      }
    ];
    setRules(ruleTemplates);
  };

  // Live data: Find archival candidates from "nodes" based on rules
  const fetchArchivalCandidates = async () => {
    try {
      const now = new Date();
      const oneYearAgo = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
      const { data: nodes, error } = await supabase
        .from('nodes')
        .select(`
          id, title, created_at, updated_at, locked, status
        `)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch compliance violations for more accurate flags
      const { data: violations } = await supabase
        .from('compliance_violations')
        .select('content_id, violation_type, severity, status');

      // Map content nodes -> candidates
      const candidates: ArchivalCandidate[] = (nodes ?? []).map((node: any) => {
        // Archive if older than 1 year
        let reason = "";
        let recommended_action = "";
        const legal_flags: string[] = [];

        // Find any severe flags on this content
        const nodeViolations = (violations ?? []).filter(v => v.content_id === node.id);
        nodeViolations.forEach(v => {
          if (v.severity === "critical" || v.status === "open") {
            legal_flags.push(`${v.violation_type}_${v.severity}`);
          }
        });

        if (new Date(node.created_at) < oneYearAgo) {
          reason = "Content is over 1 year old";
          recommended_action = 'archive';
        } else if (legal_flags.length > 0) {
          reason = "Active compliance/critical legal flags";
          recommended_action = 'flag_review';
        } else if (node.locked) {
          reason = "Content is already locked";
          recommended_action = 'lock';
        } else {
          // Not usually a candidate
          reason = "";
          recommended_action = "";
        }

        if (!recommended_action) return null;

        return {
          content_id: node.id,
          title: node.title,
          created_date: node.created_at,
          last_accessed: node.updated_at,
          compliance_score: 100, // Placeholder; real score needs content_quality or similar metric!
          legal_flags,
          recommended_action,
          reason,
        };
      }).filter(Boolean) as ArchivalCandidate[];

      setCandidates(candidates);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch archival candidates",
        variant: "destructive",
      });
    }
  };

  const createRule = async () => {
    if (!newRule.name || !newRule.trigger_value) {
      toast({
        title: "Incomplete Rule",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      const rule: ArchivalRule = {
        id: Date.now().toString(),
        ...newRule,
        is_active: true,
        last_run: new Date().toISOString(),
        items_processed: 0
      };

      setRules(prev => [...prev, rule]);
      setNewRule({
        name: '',
        description: '',
        trigger_type: 'age',
        trigger_value: '',
        action: 'archive'
      });
      setIsCreatingRule(false);

      toast({
        title: "Rule Created",
        description: "Archival rule created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create archival rule",
        variant: "destructive",
      });
    }
  };

  const toggleRule = async (ruleId: string) => {
    setRules(prev => prev.map(rule => 
      rule.id === ruleId 
        ? { ...rule, is_active: !rule.is_active }
        : rule
    ));

    toast({
      title: "Rule Updated",
      description: "Archival rule status updated",
    });
  };

  const runRule = async (ruleId: string) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;

    try {
      // Simulate running the rule
      await new Promise(resolve => setTimeout(resolve, 1000));

      setRules(prev => prev.map(r => 
        r.id === ruleId 
          ? { ...r, last_run: new Date().toISOString(), items_processed: r.items_processed + Math.floor(Math.random() * 5) }
          : r
      ));

      toast({
        title: "Rule Executed",
        description: `Archival rule "${rule.name}" executed successfully`,
      });
    } catch (error) {
      toast({
        title: "Execution Failed",
        description: "Failed to execute archival rule",
        variant: "destructive",
      });
    }
  };

  const executeAction = async (candidate: ArchivalCandidate) => {
    try {
      const actionLabels = {
        archive: 'Content archived',
        lock: 'Content locked',
        flag_review: 'Content flagged for review'
      };

      toast({
        title: "Action Executed",
        description: actionLabels[candidate.recommended_action as keyof typeof actionLabels],
      });

      // Remove from candidates list
      setCandidates(prev => prev.filter(c => c.content_id !== candidate.content_id));
    } catch (error) {
      toast({
        title: "Action Failed",
        description: "Failed to execute recommended action",
        variant: "destructive",
      });
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'archive': return <Archive className="h-4 w-4" />;
      case 'lock': return <Settings className="h-4 w-4" />;
      case 'flag_review': return <AlertTriangle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'archive': return 'bg-white text-black';
      case 'lock': return 'bg-white text-black';
      case 'flag_review': return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  return (
    <div className="space-y-6">
      {/* Rules Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Automated Archival Rules
            </CardTitle>
            <Button onClick={() => setIsCreatingRule(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isCreatingRule && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Create New Archival Rule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="rule-name">Rule Name</Label>
                    <Input
                      id="rule-name"
                      value={newRule.name}
                      onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter rule name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="trigger-type">Trigger Type</Label>
                    <Select 
                      value={newRule.trigger_type} 
                      onValueChange={(value: any) => setNewRule(prev => ({ ...prev, trigger_type: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="age">Content Age (days)</SelectItem>
                        <SelectItem value="compliance_score">Compliance Score Threshold</SelectItem>
                        <SelectItem value="legal_flag">Legal Flag Presence</SelectItem>
                        <SelectItem value="manual">Manual Trigger</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label htmlFor="trigger-value">Trigger Value</Label>
                    <Input
                      id="trigger-value"
                      value={newRule.trigger_value}
                      onChange={(e) => setNewRule(prev => ({ ...prev, trigger_value: e.target.value }))}
                      placeholder="e.g., 365 for days, 60 for score"
                    />
                  </div>

                  <div>
                    <Label htmlFor="action">Action</Label>
                    <Select 
                      value={newRule.action} 
                      onValueChange={(value: any) => setNewRule(prev => ({ ...prev, action: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="archive">Archive Content</SelectItem>
                        <SelectItem value="lock">Lock Content</SelectItem>
                        <SelectItem value="flag_review">Flag for Review</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={newRule.description}
                    onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this rule does"
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={createRule}>Create Rule</Button>
                  <Button variant="outline" onClick={() => setIsCreatingRule(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4">
            {rules.map((rule) => (
              <Card key={rule.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        {getActionIcon(rule.action)}
                        {rule.name}
                      </h4>
                      <p className="text-sm text-black">{rule.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getActionColor(rule.action)}>
                        {rule.action.replace('_', ' ')}
                      </Badge>
                      <Switch 
                        checked={rule.is_active}
                        onCheckedChange={() => toggleRule(rule.id)}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3 text-sm">
                    <div>
                      <span className="font-medium">Trigger:</span> {rule.trigger_type} = {rule.trigger_value}
                    </div>
                    <div>
                      <span className="font-medium">Last Run:</span> {new Date(rule.last_run).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Processed:</span> {rule.items_processed} items
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => runRule(rule.id)}>
                      <Play className="h-3 w-3 mr-1" />
                      Run Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Archival Candidates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Archival Candidates ({candidates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {candidates.map((candidate) => (
              <Card key={candidate.content_id} className="border-l-4 border-l-yellow-500">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium">{candidate.title}</h4>
                      <p className="text-sm text-black">{candidate.reason}</p>
                    </div>
                    <Badge className={getActionColor(candidate.recommended_action)}>
                      {getActionIcon(candidate.recommended_action)}
                      {candidate.recommended_action.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div className="grid gap-4 md:grid-cols-4 text-sm mb-3">
                    <div>
                      <span className="font-medium">Created:</span> {new Date(candidate.created_date).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Last Accessed:</span> {new Date(candidate.last_accessed).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Compliance:</span> {candidate.compliance_score}%
                    </div>
                    <div>
                      <span className="font-medium">Legal Flags:</span> {candidate.legal_flags.length}
                    </div>
                  </div>

                  {candidate.legal_flags.length > 0 && (
                    <div className="flex gap-1 mb-3">
                      {candidate.legal_flags.map((flag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {flag.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <Button 
                    size="sm" 
                    onClick={() => executeAction(candidate)}
                    className="flex items-center gap-2"
                  >
                    {getActionIcon(candidate.recommended_action)}
                    Execute {candidate.recommended_action.replace('_', ' ')}
                  </Button>
                </CardContent>
              </Card>
            ))}

            {candidates.length === 0 && (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <p className="text-black">No content currently requires archival action</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomatedArchivalSystem;
